// frontend/src/components/AlertsPanel.jsx
import React from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

/** Threshold (%) at which a usage metric becomes an alert. */
const WARN_AT = 80;
const DANGER_AT = 95;

/**
 * AlertsPanel — the dashboard's "needs attention" digest. Surfaces only the
 * plan/usage thresholds the org is close to or over, each with a clear CTA, and
 * a graceful all-clear empty state (PRIVATE-AREA-PLAN PA2-T5).
 *
 * Derived entirely from the usage payload the dashboard already fetches — no
 * extra request. (Code-expiry alerts are deferred: they need a dedicated
 * endpoint that does not exist yet.)
 *
 * @param {object} props.usage GET /api/orgs/me/usage payload, or null.
 */
function AlertsPanel({ usage }) {
  const pct = usage?.percentages || {};
  const metrics = [
    { key: "scans_pct", label: "monthly scans", value: pct.scans_pct },
    { key: "codes_pct", label: "active codes", value: pct.codes_pct },
    { key: "api_calls_pct", label: "API calls", value: pct.api_calls_pct },
  ];

  const alerts = metrics
    .filter((m) => typeof m.value === "number" && m.value >= WARN_AT)
    .map((m) => ({
      ...m,
      level: m.value >= DANGER_AT ? "danger" : "warn",
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">Alerts</h2>

      {alerts.length === 0 ? (
        <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
          <CheckCircleIcon className="h-5 w-5 text-accent-500" />
          You&rsquo;re all set — nothing needs attention.
        </div>
      ) : (
        <ul className="space-y-2">
          {alerts.map((a) => {
            const danger = a.level === "danger";
            return (
              <li
                key={a.key}
                className={`flex items-start gap-2 rounded-lg p-2.5 text-sm ${
                  danger ? "bg-red-50" : "bg-yellow-50"
                }`}
              >
                <ExclamationTriangleIcon
                  className={`mt-0.5 h-4 w-4 shrink-0 ${
                    danger ? "text-red-500" : "text-yellow-500"
                  }`}
                />
                <div className="flex-1">
                  <p className={danger ? "text-red-800" : "text-yellow-800"}>
                    You&rsquo;ve used <strong>{Math.round(a.value)}%</strong> of your{" "}
                    {a.label} this period.
                  </p>
                  <Link
                    to="/billing"
                    className="font-semibold text-brand-700 hover:text-brand-800"
                  >
                    Upgrade for more headroom →
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

AlertsPanel.propTypes = { usage: PropTypes.object };
AlertsPanel.defaultProps = { usage: null };

export default AlertsPanel;
